import { useGradePreference } from "@shared/hooks";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { MdArrowDropDown, MdArrowDropUp } from "react-icons/md";
import {
  type DifficultyGrade,
  getGradeDisplayByPreference,
  getSuggestedDifficulties,
  parseDifficulty,
} from "../../utils/difficultyConverter";
import styles from "./difficulty-input.module.scss";

interface DifficultyInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  label?: string;
  helperText?: string;
}

// NOTE: I am assuming you don't have access to the UI library's components
// and am using native elements as per the original code.
// If you have <Input> and <Text>, you can swap them back in.
export const DifficultyInput: React.FC<DifficultyInputProps> = ({
  value,
  onChange,
  placeholder = "e.g., V5, 7A, V6 / 7A",
  disabled = false,
  required = false,
  error,
  label,
  helperText,
}) => {
  const gradePreference = useGradePreference();
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<DifficultyGrade[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(value || "");

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const newSuggestions = getSuggestedDifficulties(inputValue, 23); // Show all 23 universal grades (0-22)
    setSuggestions(newSuggestions);
    setSelectedIndex(-1);
  }, [inputValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(newValue.length > 0);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // When focused with empty input, show all grades
    if (!inputValue.trim()) {
      const allGrades = getSuggestedDifficulties("", 23);
      setSuggestions(allGrades);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
      case "Tab":
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          event.preventDefault();
          selectSuggestion(suggestions[selectedIndex]);
        } else {
          setIsOpen(false);
        }
        break;
      case "Escape":
        event.preventDefault();
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const selectSuggestion = (grade: DifficultyGrade) => {
    const newValue = grade.display;
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      inputRef.current?.focus();
    }
  };

  const currentGrade = parseDifficulty(inputValue);
  const hasValidGrade = currentGrade !== null;

  return (
    <div className={styles.container}>
      {label && (
        <label htmlFor="difficulty-input" className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <input
            ref={inputRef}
            id="difficulty-input"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className={`${styles.input} ${error ? styles.error : ""} ${
              hasValidGrade ? styles.valid : ""
            }`}
            autoComplete="off"
          />
        </div>

        <button
          type="button"
          className={styles.dropdownToggle}
          onClick={toggleDropdown}
          disabled={disabled}
          tabIndex={-1}
        >
          {isOpen ? <MdArrowDropUp size={20} /> : <MdArrowDropDown size={20} />}
        </button>

        {isOpen && suggestions.length > 0 && (
          <div ref={dropdownRef} className={styles.dropdown}>
            {suggestions.map((grade, index) => (
              <button
                key={grade.universal}
                type="button"
                className={`${styles.suggestion} ${
                  index === selectedIndex ? styles.selected : ""
                }`}
                onClick={() => selectSuggestion(grade)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className={styles.suggestionContent}>
                  <span className={styles.mainGrade}>
                    {getGradeDisplayByPreference(
                      grade.universal,
                      gradePreference
                    )}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <p className={styles.errorText}>{error}</p>}
      {helperText && !error && (
        <p className={styles.helperText}>{helperText}</p>
      )}
    </div>
  );
};
